import { Injectable } from "@angular/core";
import {
    HttpErrorResponse,
    HttpEvent,
    HttpEventType,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { LoginService } from "../services/login.service";
import { environment } from "src/environments/environment";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly apiBaseUrl = this.normalizeBaseUrl(environment.baseurl);
    private readonly storageBaseUrl = this.normalizeBaseUrl(environment.storageBaseUrl);

    constructor(private loginService: LoginService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let modifiedRequest = req;
        const requestToken = this.loginService.getToken();

        const isLoginRequest = req.url.includes('/login');
        const isLogoutRequest = req.url.includes('/logout');
        if (!isLoginRequest && this.shouldAttachAuthorization(req.url)) {
            const token = requestToken;
            if (token) {
                modifiedRequest = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }
        }

        return next.handle(modifiedRequest).pipe(
            tap((event) => {
                if (
                    !isLoginRequest &&
                    !isLogoutRequest &&
                    this.shouldAttachAuthorization(req.url) &&
                    !!requestToken &&
                    requestToken === this.loginService.getToken() &&
                    event.type === HttpEventType.Response
                ) {
                    this.loginService.markSessionActivity();
                }
            }),
            catchError((error: HttpErrorResponse) => {
                const isCurrentSessionRequest = !isLoginRequest && !isLogoutRequest &&
                    this.shouldAttachAuthorization(req.url) && !!requestToken &&
                    requestToken === this.loginService.getToken() && this.loginService.isLoggedIn();
                if (isCurrentSessionRequest && error.status === 401) {
                    void this.loginService.handleSessionTimeout('TIMEOUT');
                } else if (isCurrentSessionRequest && error.status === 440) {
                    void this.loginService.handleSessionTimeout('INACTIVITY');
                }
                return throwError(() => error);
            })
        );
    }

    private shouldAttachAuthorization(url: string): boolean {
        if (!url) {
            return false;
        }

        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return false;
        }

        if (this.isStorageRequest(trimmedUrl)) {
            return false;
        }

        if (!this.apiBaseUrl) {
            return !this.isAbsoluteUrl(trimmedUrl);
        }

        if (!this.isAbsoluteUrl(trimmedUrl)) {
            const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
            return normalizedPath.startsWith(this.apiBaseUrlPathname());
        }

        return this.stripTrailingSlash(trimmedUrl).startsWith(this.apiBaseUrl);
    }

    private isStorageRequest(url: string): boolean {
        if (!this.storageBaseUrl) {
            return false;
        }

        return this.stripTrailingSlash(url).startsWith(this.storageBaseUrl);
    }

    private isAbsoluteUrl(url: string): boolean {
        return /^https?:\/\//i.test(url);
    }

    private normalizeBaseUrl(value: string | undefined | null): string | null {
        if (typeof value !== "string") {
            return null;
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        const withoutTrailingSlash = this.stripTrailingSlash(trimmed);
        return withoutTrailingSlash || null;
    }

    private stripTrailingSlash(value: string): string {
        return value.replace(/\/+$/, "");
    }

    private apiBaseUrlPathname(): string {
        if (!this.apiBaseUrl) {
            return "/";
        }

        try {
            const url = new URL(this.apiBaseUrl);
            const pathname = url.pathname || "/";
            return pathname.endsWith("/") ? pathname : `${pathname}/`;
        } catch (error) {
            const normalized = this.apiBaseUrl.startsWith("/") ? this.apiBaseUrl : `/${this.apiBaseUrl}`;
            return normalized.endsWith("/") ? normalized : `${normalized}/`;
        }
    }
}
